/**
 * RealtimeProvider - Manages real-time event connections and integrations.
 *
 * This component:
 * 1. Establishes the SSE connection when mounted
 * 2. Auto-refreshes session list when sessions change
 * 3. Provides connection status for UI indicators
 * 4. Handles cleanup on unmount
 *
 * Place this at the top level of your authenticated app tree.
 */

import { useEffect, useCallback, useRef } from 'react'
import { useRealtimeConnection, useRealtimeEvent } from '@/hooks/useRealtime'
import { useChatStore } from '@/store/chatStore'
import type { DbChangeEvent, MessageAddedEvent } from '@/store/realtimeStore'

interface RealtimeProviderProps {
  children: React.ReactNode
  /** Show connection indicator in corner (default: true in development) */
  showDebugIndicator?: boolean
}

/**
 * Debounce helper to prevent rapid successive calls.
 */
function useDebouncedCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const callbackRef = useRef(callback)

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args)
      }, delay)
    }) as T,
    [delay]
  )
}

export function RealtimeProvider({
  children,
  showDebugIndicator = import.meta.env.DEV,
}: RealtimeProviderProps) {
  // Initialize realtime connection
  const {
    connectionState,
    isConnected,
    eventsReceived,
  } = useRealtimeConnection()

  // Chat store actions
  const loadSessions = useChatStore((state) => state.loadSessions)
  const updateSessionMeta = useChatStore((state) => state.updateSessionMeta)
  const addMessageFromEvent = useChatStore((state) => state.addMessageFromEvent)
  const sessions = useChatStore((state) => state.sessions)

  // Debounced session refresh (prevents rapid re-fetches)
  const debouncedLoadSessions = useDebouncedCallback(loadSessions, 500)

  // Handle session changes
  useRealtimeEvent<DbChangeEvent>(
    'db_session_changed',
    useCallback(
      (event) => {
        const { operation, id, data } = event.data

        console.log('[Realtime] Session changed:', operation, id, data)

        if (operation === 'INSERT') {
          // New session created - refresh the list to include it
          debouncedLoadSessions()
        } else if (operation === 'UPDATE') {
          // Session updated - optimistically update if we have it
          const sessionId = String(id)
          const existingSession = sessions.find((s) => s.id === sessionId)

          if (existingSession) {
            // Optimistic update
            updateSessionMeta(sessionId, {
              summary: data.summary as string | undefined,
            })
          } else {
            // Unknown session, refresh list
            debouncedLoadSessions()
          }
        } else if (operation === 'DELETE') {
          // Session deleted - refresh to remove it
          debouncedLoadSessions()
        }
      },
      [sessions, updateSessionMeta, debouncedLoadSessions]
    ),
    [sessions, updateSessionMeta, debouncedLoadSessions]
  )

  // Handle message_added events for cross-client synchronization
  useRealtimeEvent<MessageAddedEvent>(
    'message_added',
    useCallback(
      (event) => {
        const sessionId = event.session_id
        if (!sessionId) return

        const { message_id, role, content, tool_calls, timestamp } = event.data

        console.log('[Realtime] Message added:', message_id, role, sessionId)

        // Convert tool_calls to the format expected by chatStore
        const convertedToolCalls = tool_calls?.map((tc) => ({
          name: tc.name,
          arguments: tc.arguments || {},
          result: tc.result,
          status: 'success' as const,
        }))

        addMessageFromEvent(
          sessionId,
          message_id,
          role,
          content,
          convertedToolCalls,
          timestamp ? new Date(timestamp) : undefined
        )
      },
      [addMessageFromEvent]
    ),
    [addMessageFromEvent]
  )

  return (
    <>
      {children}

      {/* Debug indicator for development */}
      {showDebugIndicator && (
        <div
          className="fixed bottom-2 right-2 z-50 flex items-center gap-2 px-2 py-1 rounded-full text-xs font-mono bg-surface/80 backdrop-blur border border-surface-border shadow-lg"
          title={`Connection: ${connectionState}\nEvents: ${eventsReceived}`}
        >
          <div
            className={`w-2 h-2 rounded-full ${
              isConnected
                ? 'bg-green-500 animate-pulse'
                : connectionState === 'connecting' || connectionState === 'reconnecting'
                ? 'bg-yellow-500 animate-pulse'
                : 'bg-red-500'
            }`}
          />
          <span className="text-text-muted">
            {connectionState === 'connected'
              ? `Live (${eventsReceived})`
              : connectionState}
          </span>
        </div>
      )}
    </>
  )
}

export default RealtimeProvider
